import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { SparkleIcon } from '../atoms/SparkleIcon';
import { AiFeedback } from './AiFeedback';
import type { AiFeedbackSentiment } from '../../types/residents';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '../ui/tooltip';

const ENABLE_CAREPLAN_STATUS: boolean = false;

interface ProgressNoteCardProps {
  dateTime: string;
  author: string;
  credentials: string;
  riskTags: string[];
  content: string;
  careplanStatus: 'not-planned' | 'in-care-plan';
  reasoning: string | null;
  /** UI-visibility-only switch — reasoning is still generated/stored when false. */
  reasoningVisible?: boolean;
  noteId?: string;
  residentId?: string;
  riskDocumentId?: string;
  wqId?: string;
  insightsDays?: number;
  existingReasoningFeedback?: AiFeedbackSentiment | null;
  existingReasoningComment?: string | null;
  existingClassificationFeedback?: AiFeedbackSentiment | null;
  existingClassificationComment?: string | null;
}

export const ProgressNoteCard: React.FC<ProgressNoteCardProps> = ({
  dateTime,
  author,
  credentials,
  riskTags,
  content,
  careplanStatus,
  reasoning,
  reasoningVisible = false,
  noteId,
  residentId,
  riskDocumentId,
  wqId,
  insightsDays,
  existingReasoningFeedback,
  existingReasoningComment,
  existingClassificationFeedback,
  existingClassificationComment,
}) => {
  const [showReasoning, setShowReasoning] = useState(false);

  const closeReasoning = useCallback(() => setShowReasoning(false), []);

  // Close on Escape key
  useEffect(() => {
    if (!showReasoning) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeReasoning();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showReasoning, closeReasoning]);

  return (
    <>
      <div className="border-l-4 border-[#228189] bg-[#FEF3E2] rounded p-4 mb-3">
        {/* Date/Time + Author */}
        <div className="text-xs text-gray-600 mb-0.5">{dateTime}</div>
        <div className="flex justify-between items-start gap-3 mb-3">
          <div className="shrink-0">
            <div className="text-xs text-gray-600">{author}, {credentials} &bull; {credentials === 'RN' ? 'Registered Nurse' : credentials === 'LPN' ? 'Licensed Practical Nurse' : credentials === 'CNA' ? 'Certified Nursing Assistant' : credentials}</div>
          </div>
          {/* Risk Tags - Outlined style per Figma */}
          <div className="flex gap-2 flex-wrap justify-end shrink-0 items-center">
            {riskTags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 border border-[#B02828] text-[#B02828] text-[11px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap"
              >
                <AlertTriangle className="w-3 h-3 shrink-0" />
                {tag}
              </span>
            ))}
            {noteId && residentId && riskDocumentId && riskTags.length > 0 && (
              <AiFeedback
                residentId={residentId}
                contentType="note_risk_classification"
                riskDocumentId={riskDocumentId}
                sourceItemId={noteId}
                wqId={wqId}
                insightsDays={insightsDays}
                existingSentiment={existingClassificationFeedback}
                existingComment={existingClassificationComment}
              />
            )}
          </div>
        </div>
        {/* Note Content with AI Reasoning icon */}
        <div className="flex items-start gap-2 mb-3">
          <div className="text-sm leading-relaxed text-gray-800 flex-1">{content}</div>
          {reasoning && reasoningVisible && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setShowReasoning(true)}
                  className="shrink-0 mt-0.5 text-[#97449C] hover:text-[#7a3680] transition-colors cursor-pointer"
                  aria-label="View AI reasoning"
                >
                  <SparkleIcon className="w-7 h-7" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={4}>
                Click to see AI reasoning
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {/* Footer - Care Plan Status (Hidden until feature implemented) */}
        {ENABLE_CAREPLAN_STATUS && (
          <div className="flex justify-start">
            {careplanStatus === 'not-planned' ? (
              <div className="flex items-center gap-1.5 text-[#B02828]">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-xs font-medium">Not Care Planned</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-[#23833F]">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-xs font-medium">In Care Plan</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI Reasoning Modal — inline fixed overlay (no portal) */}
      {showReasoning && (
        <div
          className="fixed inset-0 z-9999 flex items-center justify-center p-6"
          onClick={closeReasoning}
          role="dialog"
          aria-modal="true"
          aria-label="AI Clinical Reasoning"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70" />

          {/* Dialog */}
          <div
            className="relative z-10 rounded-sm border border-ai bg-ai-background shadow-xl p-6 w-full max-w-[90%] max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={closeReasoning}
              className="absolute top-4 right-4 opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-2 mb-1">
              <SparkleIcon className="h-7 w-7 text-ai" />
              <h3 className="text-base font-medium text-ai-foreground">
                AI Clinical Reasoning
              </h3>
            </div>
            <p className="text-sm text-gray-500 mb-4 pl-9">
              {dateTime} &bull; {author}, {credentials}
            </p>

            {/* Reasoning text */}
            <div className="pl-9">
              <p className="text-sm leading-relaxed text-ai-foreground whitespace-pre-line">
                {reasoning}
              </p>
              {residentId && riskDocumentId && (
                <div className="mt-3 flex justify-end">
                  <AiFeedback
                    residentId={residentId}
                    contentType="note_reasoning"
                    riskDocumentId={riskDocumentId}
                    sourceItemId={noteId}
                    wqId={wqId}
                    insightsDays={insightsDays}
                    existingSentiment={existingReasoningFeedback}
                    existingComment={existingReasoningComment}
                  />
                </div>
              )}
            </div>

            {/* Risk tags */}
            {riskTags.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-4 pl-9">
                {riskTags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 border border-[#B02828] text-[#B02828] text-[11px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap"
                  >
                    <AlertTriangle className="w-3 h-3 shrink-0" />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
